import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";
import { Observable, Subject, Subscription } from "rxjs";
import {
    distinctUntilChanged,
    filter,
    map,
    withLatestFrom,
} from "rxjs/operators";

import { MapEvent, MarkerData, MarkerIcon } from "@hermes/aphrodite/map";
import { Coordinates } from "@hermes/api-model-common";
import { Shop } from "@hermes/api-model-shop";
import { LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import {
    LoadGeoLibrariesService,
    PIN_DARK,
    PIN_WHITE,
} from "@hermes/map-utils";
import { getCoordinatesBasedOnLocale } from "@hermes/shop-utils";
import {
    calculateVisibleShopsByCountry,
    calculateVisibleShopsByCountryAndCity,
    HoveredShopEventOrigin,
    isCity,
    isCoordinates,
    isCountry,
    isDefinedSelectedLocation,
    ShopLocatorFacade,
    ShopLocatorMode,
    ShopsStateData,
} from "@hermes/states/shop-locator";

@Component({
    selector: "h-shop-locator-map",
    templateUrl: "./shop-locator-map.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ShopLocatorMapComponent implements OnInit, OnDestroy {
    @Output()
    public clickedShop: EventEmitter<Shop> = new EventEmitter<Shop>();

    public mapEvent$!: Observable<MapEvent>;

    public markers: MarkerData[] = [];

    public markersCoordinatesInBounds$: Subject<MarkerData[]> = new Subject();

    public hoveredMarkerCoordinates$: Subject<Coordinates> = new Subject();

    public clickedMarkerCoordinates$: Subject<Coordinates> = new Subject();

    public displayZoom$: Observable<boolean>;

    public isDefinedSelectedLocation$ =
        this.shopLocatorFacade.selectedLocation$.pipe(
            map(isDefinedSelectedLocation),
        );

    private readonly subscription: Subscription = new Subscription();

    constructor(
        public shopLocatorFacade: ShopLocatorFacade,
        public loadGeoLibrariesService: LoadGeoLibrariesService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        @Inject(LOCALE) private readonly locale: Locale,
    ) {
        this.displayZoom$ = this.shopLocatorFacade.mode$.pipe(
            map(
                (mode) =>
                    mode === ShopLocatorMode.Map ||
                    mode === ShopLocatorMode.MapMobile,
            ),
        );
    }

    public ngOnInit(): void {
        this.subscription.add(
            this.shopLocatorFacade.shopsWithIgnored$
                .pipe(
                    map((shops) =>
                        this.coordinatesToMarkers(
                            this.shopsToCoordinates(shops),
                        ),
                    ),
                )
                .subscribe((markers: MarkerData[]) => {
                    this.markers = markers;
                    this.mapEventManagement();
                    this.markersManagement();
                }),
        );
    }

    public handleUnhoveredMarkerCoordinates(): void {
        this.shopLocatorFacade.unsetHoveredShop();
        this.hoveredMarkerCoordinates$.next(new Coordinates());
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private generateMarkerIcon(
        svgConstUrl: typeof PIN_WHITE | typeof PIN_DARK,
        width: number,
        height: number,
    ): MarkerIcon {
        return {
            url: this.locale.urlPrefix + svgConstUrl,
            size: { width, height },
        };
    }

    private shopsToCoordinates(shops: ShopsStateData): Coordinates[] {
        return Object.values(shops)
            .filter((shop) => {
                const coordinates: Coordinates = getCoordinatesBasedOnLocale(
                    shop,
                    this.locale,
                );

                return (
                    coordinates &&
                    (coordinates.longitude !== 0 || coordinates.latitude !== 0)
                );
            })
            .map((shop) => getCoordinatesBasedOnLocale(shop, this.locale));
    }

    private coordinatesToMarkers(coordinatesList: Coordinates[]): MarkerData[] {
        return coordinatesList.map((coordinates) => ({
            coordinates,
            icon: this.generateMarkerIcon(PIN_DARK, 23, 30),
        }));
    }

    private changeHoveredMarker(
        hoveredShopCoordinates: Coordinates | undefined,
    ) {
        /**
         * To avoid mutating the table, we prefer to re-create one.
         * The user have to use a "trackBy" to avoid re-generating everything !
         */
        this.markers = [...this.markers].map((marker) => {
            if (marker.icon.url === this.locale.urlPrefix + PIN_WHITE) {
                marker.icon = this.generateMarkerIcon(PIN_DARK, 23, 30);
            }

            if (
                hoveredShopCoordinates &&
                marker.coordinates.latitude ===
                    hoveredShopCoordinates.latitude &&
                marker.coordinates.longitude ===
                    hoveredShopCoordinates.longitude
            ) {
                marker.icon = this.generateMarkerIcon(PIN_WHITE, 23, 30);
            }

            return marker;
        });

        this.changeDetectorRef.detectChanges();
    }

    private getShopByCoordinates(
        shops: ShopsStateData,
        coordinates: Coordinates,
    ): Shop | undefined {
        return Object.values(shops).find((shop) => {
            const coords: Coordinates = getCoordinatesBasedOnLocale(
                shop,
                this.locale,
            );
            return (
                coords.latitude === coordinates.latitude &&
                coords.longitude === coordinates.longitude
            );
        });
    }

    private getShopIdByCoordinates(
        shops: ShopsStateData,
        coordinates: Coordinates,
    ): string | undefined {
        return this.getShopByCoordinates(shops, coordinates)?.shopId;
    }

    private mapEventManagement(): void {
        this.mapEvent$ = this.shopLocatorFacade.selectedLocation$.pipe(
            filter(
                (selectedLocation) =>
                    isCity(selectedLocation) ||
                    isCountry(selectedLocation) ||
                    isCoordinates(selectedLocation),
            ),
            filter(isDefinedSelectedLocation),
            withLatestFrom(this.shopLocatorFacade.shopsWithIgnored$),
            map(([selectedLocation, shops]) => {
                if (isCity(selectedLocation)) {
                    return {
                        mode: "specific-points",
                        points: this.shopsToCoordinates(
                            calculateVisibleShopsByCountryAndCity(
                                shops,
                                selectedLocation.countryCode,
                                selectedLocation.city,
                            ),
                        ),
                    };
                }
                if (isCountry(selectedLocation)) {
                    return {
                        mode: "specific-points",
                        points: this.shopsToCoordinates(
                            calculateVisibleShopsByCountry(
                                shops,
                                selectedLocation.countryCode,
                            ),
                        ),
                    };
                }

                return {
                    mode: "at-least-one-marker",
                    center: selectedLocation.coordinates,
                    bounds: selectedLocation.bounds,
                };
            }),
        );
    }

    private markersManagement(): void {
        // Subscription to manage the event when a new shop is hovered on the shop list or on the map.
        this.subscription.add(
            this.shopLocatorFacade.hoveredShopCoordinates$.subscribe(
                (hoveredShopCoordinates) => {
                    this.changeHoveredMarker(hoveredShopCoordinates);
                },
            ),
        );

        // Subscription to manage the event when a new marker is hovered on the map.
        /**
         * Due to the fact that MapMarkerClusterer and MapMarker components do not implement the mouseenter and mouseleave events,
         * we have to use the mouseover and mouseout events. Unfortunately, those events can be triggered multiple times because the
         * mouseover event is dependent on the html element on which it is bound, thus, if this element contains at least one child element,
         * the event is triggered again each time the mouse switches between the parent and child. Thus, we use the distinctUntilChanged pipe
         * to prevent this behaviour by ignoring the triggered event if it concerns the same shop.
         */
        this.subscription.add(
            this.hoveredMarkerCoordinates$
                .pipe(
                    withLatestFrom(this.shopLocatorFacade.shopsWithIgnored$),
                    distinctUntilChanged(
                        (
                            [previousMarkerCoordinates, shops],
                            [currentMarkerCoordinates],
                        ) =>
                            this.getShopIdByCoordinates(
                                shops,
                                previousMarkerCoordinates,
                            ) ===
                            this.getShopIdByCoordinates(
                                shops,
                                currentMarkerCoordinates,
                            ),
                    ),
                )
                .subscribe(([coordinates, shops]) => {
                    const shopId = this.getShopIdByCoordinates(
                        shops,
                        coordinates,
                    );
                    if (!shopId) {
                        return;
                    }
                    this.shopLocatorFacade.setHoveredShop(
                        shopId,
                        coordinates,
                        HoveredShopEventOrigin.Map,
                    );
                }),
        );

        // Subscription to manage the event when the map emit a new list of coordinates (visible on the map)
        this.subscription.add(
            this.markersCoordinatesInBounds$
                .pipe(withLatestFrom(this.shopLocatorFacade.shopsWithIgnored$))
                .subscribe(([markers, shops]) => {
                    const shopIdsInBounds = Object.values(shops)
                        .filter((shop) =>
                            markers
                                .map((marker) => marker.coordinates)
                                .includes(
                                    getCoordinatesBasedOnLocale(
                                        shop,
                                        this.locale,
                                    ),
                                ),
                        )
                        .map((shop) => shop.shopId);

                    this.shopLocatorFacade.setVisibleShopIds(shopIdsInBounds);
                }),
        );

        this.subscription.add(
            this.clickedMarkerCoordinates$
                .pipe(
                    withLatestFrom(this.shopLocatorFacade.shopsWithIgnored$),
                    map(([coordinates, shops]) =>
                        this.getShopByCoordinates(shops, coordinates),
                    ),
                )
                .subscribe((shop) => {
                    this.clickedShop.emit(shop);
                }),
        );
    }
}
